import React from 'react';
import { format, isSameMonth } from 'date-fns';
import { FileDown, Receipt, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import useEvents from '../hooks/useEvents';
import { useAuth } from '../hooks/useAuth';

interface BillExportProps {
  currentMonth: Date;
}

export default function BillExport({ currentMonth }: BillExportProps) {
  const { user } = useAuth();
  const { events, loading } = useEvents();
  const [isOpen, setIsOpen] = React.useState(false);

  const regularBills = React.useMemo(() => {
    if (!events) return [];

    return events
      .filter(event =>
        event.type === 'bill' &&
        !event.debtId &&
        !event.bucketId &&
        isSameMonth(event.date, currentMonth)
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [events, currentMonth]);

  const uniqueBills = React.useMemo(() => {
    const billMap = new Map();

    regularBills.forEach(bill => {
      const key = bill.recurringGroupId || bill.id;
      if (!billMap.has(key)) {
        billMap.set(key, bill);
      }
    });

    return Array.from(billMap.values()).sort((a, b) =>
      a.title.localeCompare(b.title)
    );
  }, [regularBills]);

  const handleExportPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text('Bills List', 14, 20);
    doc.setFontSize(12);
    doc.text(`Month: ${format(currentMonth, 'MMMM yyyy')}`, 14, 30);
    doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')}`, 14, 37);

    const totalAmount = uniqueBills.reduce((sum, bill) => sum + bill.amount, 0);

    doc.setFontSize(14);
    doc.text('Summary', 14, 52);
    doc.setFontSize(10);
    doc.text(`Total Bills: ${uniqueBills.length}`, 14, 62);
    doc.text(`Total Amount: $${totalAmount.toFixed(2)}`, 14, 69);

    const tableData = uniqueBills.map(bill => [
      bill.title,
      `$${bill.amount.toFixed(2)}`,
      bill.category || 'N/A',
      bill.isRecurring ? (bill.frequency || 'Monthly') : 'One-time',
      bill.isAutopay ? 'Yes' : 'No',
      bill.isScheduled ? 'Yes' : 'No',
      bill.isSubscription ? 'Yes' : 'No'
    ]);

    doc.autoTable({
      startY: 75,
      head: [['Bill Name', 'Amount', 'Category', 'Frequency', 'Autopay', 'Scheduled', 'Subscription']],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [220, 38, 38] },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 25, halign: 'right' },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 20, halign: 'center' },
        6: { cellWidth: 25, halign: 'center' }
      }
    });

    doc.save(`bills_${format(currentMonth, 'yyyy-MM')}.pdf`);
  };

  const handleExportCSV = () => {
    const headers = ['Bill Name', 'Amount', 'Category', 'Frequency', 'Autopay', 'Scheduled', 'Subscription'];
    const rows = uniqueBills.map(bill => [
      bill.title,
      bill.amount.toFixed(2),
      bill.category || 'N/A',
      bill.isRecurring ? (bill.frequency || 'monthly') : 'one-time',
      bill.isAutopay ? 'Yes' : 'No',
      bill.isScheduled ? 'Yes' : 'No',
      bill.isSubscription ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bills_${format(currentMonth, 'yyyy-MM')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!user) return null;
  if (loading) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
      >
        <FileDown className="w-4 h-4 mr-2" />
        Export Bills
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Receipt className="w-6 h-6 mr-2" />
                  Export Bills List
                </h2>
                <p className="text-sm text-gray-500 mt-1">{format(currentMonth, 'MMMM yyyy')}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-sm text-blue-700">
                  This list includes only regular bills from {format(currentMonth, 'MMMM yyyy')} and excludes debt snowball payments and budget bucket allocations.
                  {uniqueBills.length > 0 && ` Found ${uniqueBills.length} bill(s) for this month.`}
                </p>
              </div>

              {uniqueBills.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No bills found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add bills to your calendar to export them.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bill Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Frequency
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Autopay
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Scheduled
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subscription
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {uniqueBills.map((bill) => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {bill.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${bill.amount.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {bill.category || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {bill.isRecurring ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {bill.frequency || 'monthly'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                one-time
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {bill.isAutopay ? '✓' : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {bill.isScheduled ? '✓' : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {bill.isSubscription ? '✓' : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Summary</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Total Bills This Month:</span>
                        <span className="ml-2 font-semibold">{uniqueBills.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Amount:</span>
                        <span className="ml-2 font-semibold text-red-600">
                          ${uniqueBills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {uniqueBills.length > 0 && (
              <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export CSV
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
